import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import { authApi, type LoginPayload, type RegisterPayload } from "@/api";
import router from "@/router";

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  avatarUrl: string | null;
  companyId: string;
  company: {
    id: string;
    name: string;
    slug: string;
    plan: string;
    subscriptionStatus: string;
  };
}

export const useAuthStore = defineStore("auth", () => {
  const user = ref<User | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(
    () => !!user.value && !!localStorage.getItem("access_token"),
  );
  const isAdmin = computed(() =>
    ["SUPER_ADMIN", "COMPANY_ADMIN"].includes(user.value?.role ?? ""),
  );
  const canAnalyze = computed(() =>
    ["SUPER_ADMIN", "COMPANY_ADMIN", "ANALYST"].includes(
      user.value?.role ?? "",
    ),
  );
  const role = computed(() => user.value?.role ?? null);

  async function login(payload: LoginPayload) {
    loading.value = true;
    error.value = null;
    try {
      const { data } = await authApi.login(payload);
      const { tokens, user: userData } = data.data;
      localStorage.setItem("access_token", tokens.accessToken);
      localStorage.setItem("refresh_token", tokens.refreshToken);
      user.value = userData;
      await router.push("/dashboard");
      // Load DB-persisted theme for this user (fire-and-forget)
      import("@/stores/theme")
        .then(({ useThemeStore }) => useThemeStore().syncFromServer())
        .catch(() => {});
      // Load feature flags now that the user is authenticated (fire-and-forget)
      import("@/stores/features")
        .then(({ useFeatureStore }) => useFeatureStore().load())
        .catch(() => {});
      // Load region settings (currency, timezone, etc.) for locale-aware formatting
      import("@/stores/region")
        .then(({ useRegionStore }) => useRegionStore().fetchRegionSettings())
        .catch(() => {});
    } catch (err: any) {
      const status = err.response?.status;
      const body = err.response?.data;
      if (status === 401) {
        error.value = "Invalid email or password.";
      } else if (status === 422 && body?.errors?.length) {
        error.value = body.errors
          .map((e: any) => e.message ?? e.msg ?? e)
          .join(" ");
      } else {
        error.value =
          body?.error ?? body?.message ?? "Login failed. Please try again.";
      }
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function register(payload: RegisterPayload) {
    loading.value = true;
    error.value = null;
    try {
      const { data } = await authApi.register(payload);
      const { tokens, user: userData } = data.data;
      localStorage.setItem("access_token", tokens.accessToken);
      localStorage.setItem("refresh_token", tokens.refreshToken);
      user.value = userData;
      await router.push("/dashboard");
      // Load DB-persisted theme for this user (fire-and-forget)
      import("@/stores/theme")
        .then(({ useThemeStore }) => useThemeStore().syncFromServer())
        .catch(() => {});
      // Load feature flags now that the user is authenticated (fire-and-forget)
      import("@/stores/features")
        .then(({ useFeatureStore }) => useFeatureStore().load())
        .catch(() => {});
      // Load region settings (currency, timezone, etc.) for locale-aware formatting
      import("@/stores/region")
        .then(({ useRegionStore }) => useRegionStore().fetchRegionSettings())
        .catch(() => {});
    } catch (err: any) {
      const status = err.response?.status;
      const body = err.response?.data;
      if (status === 422 && body?.errors?.length) {
        error.value = body.errors
          .map((e: any) => e.message ?? e.msg ?? e)
          .join(" ");
      } else if (status === 409) {
        error.value = "Email or company slug already in use.";
      } else {
        error.value =
          body?.error ??
          body?.message ??
          "Registration failed. Please try again.";
      }
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    const refreshToken = localStorage.getItem("refresh_token");
    if (refreshToken) {
      await authApi.logout(refreshToken).catch(() => {});
    }
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    user.value = null;
    await router.push("/auth/login");
  }

  async function tryRestoreSession() {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    try {
      const { data } = await authApi.me();
      user.value = data.data;
      // Hydrate region settings (fire-and-forget)
      import("@/stores/region")
        .then(({ useRegionStore }) => useRegionStore().fetchRegionSettings())
        .catch(() => {});
    } catch {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
    }
  }

  // when user role or company plan changes we need to refresh feature flags
  // and clear cached navigation so the sidebar reflects the new context.
  watch(
    () => [user.value?.company?.plan, user.value?.role],
    () => {
      import("@/stores/features")
        .then(({ useFeatureStore }) => useFeatureStore().load())
        .catch(() => {});
      import("@/utils/filterNavigation")
        .then(({ invalidateNavCache }) => invalidateNavCache())
        .catch(() => {});
    },
  );

  return {
    user,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    canAnalyze,
    role,
    login,
    register,
    logout,
    tryRestoreSession,
  };
});
